import {useEffect, useState} from 'react';

import {TypeLocalNameEnum} from 'domain/usecases';
import {FeaturesEnum, HomeEnum, QueryParamsEnum, QueryParamsForTaggingEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useGeolocation} from 'presentation/hooks/useGeolocation';
import {usePermissions} from 'presentation/hooks/usePermissions';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {ErrorCode} from 'presentation/hooks/useGeolocation/types';

import {FlowEnum} from 'presentation/pages/private/booking/constants';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {FlowTypeEnum, getUfPath} from 'presentation/utils';
import {useNavigate} from 'react-router-dom';
import {HandlePermissionsProps} from '../../types';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useCoordinatesStore} from 'presentation/modules/booking/shared/store/useCoordinatesStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useGetCityLocationAndVerifySpecialtiesQuery} from 'presentation/modules/booking/shared/queries/useGetCityLocationAndVerifySpecialtiesQuery';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';

const {permissionsNotAllowed} = translations['pt-br'].commons;

export function useLocation(flowType: FlowTypeEnum) {
	const {isLaptop} = useMediaQueries();

	const bookingUfPath = getUfPath(flowType);
	const navigate = useNavigate();
	const [openInstructions, setOpenInstructions] = useState(false);
	const [openInstabilityError, setOpenInstabilityError] = useState(false);
	const {getPosition, resetCoords, coords, error: geolocationError} = useGeolocation();
	const {
		permission,
		checkPermission,
		error: permissionError,
	} = usePermissions({permissionName: 'geolocation'});
	const {currentEmail, currentPhone} = usePersonalData();
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const geolocationOrPermissionIsNotSupported =
		geolocationError?.code === ErrorCode.NOT_SUPPORTED ||
		permissionError === permissionsNotAllowed;
	const positionUnavailableOrTimeout =
		geolocationError?.code === ErrorCode.POSITION_UNAVAILABLE ||
		geolocationError?.code === ErrorCode.TIMEOUT;
	const coordinatesValue = coords?.lat && coords?.lng ? `${coords?.lat},${coords?.lng}` : '';
	const {setCoordinates} = useCoordinatesStore();
	const [showPermissionDeniedSnackbar, setShowPermissionDeniedSnackbar] = useState(false);
	const [getPositionClicked, setGetPositionClicked] = useState(0);
	const [openGetPositionLoading, setOpenGetPositionLoading] = useState(false);
	const {setSearchParams} = useQueryParams();

	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isHealthFlow = flowType === FlowEnum.HEALTH;

	const {isEnabled: isGeolocationEnabled} = useFeatureFlag(
		isOdontoFlow
			? FeaturesEnum.PORTAL_FEATURE_GEOLOC_ODONTO
			: FeaturesEnum.PORTAL_FEATURE_GEOLOC,
	);

	const {SPECIALITY_OR_EXAM_STEP} = getStepPosition({
		flowType,
		isManualLocation: bookingData.isManualLocation,
	});

	useEffect(() => {
		checkPermission();
	}, []);

	const handleGetPosition = ({state}: HandlePermissionsProps) => {
		switch (state) {
			case 'prompt':
			case 'granted':
				setGetPositionClicked(prev => prev + 1);
				resetCoords();
				getPosition();
				break;
			case 'denied':
				setOpenInstructions(true);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		setGetPositionClicked(0);
	}, [permission]);

	useEffect(() => {
		if (permission === 'granted' && getPositionClicked) {
			setOpenGetPositionLoading(true);
		}
	}, [permission, getPositionClicked]);

	const handleOpenPermissionDeniedSnackbar = () => {
		setShowPermissionDeniedSnackbar(true);
	};

	const handleClosePermissionDeniedSnackbar = () => {
		setShowPermissionDeniedSnackbar(false);
	};

	useEffect(() => {
		if (permission === 'denied' && !showPermissionDeniedSnackbar) {
			handleOpenPermissionDeniedSnackbar();
		}
	}, [permission]);

	useEffect(() => {
		setCoordinates(coords);
	}, [coords]);

	const {
		data: cityLocationAndSpecialtiesData,
		isError: isCityLocationAndSpecialtiesError,
		error: cityLocationAndSpecialtiesError,
		refetch: cityLocationAndSpecialtiesRefetch,
	} = useGetCityLocationAndVerifySpecialtiesQuery({
		latitude: coords?.lat?.toString() || '',
		longitude: coords?.lng?.toString() || '',
		flowType,
	});

	const handleGoHome = () => {
		navigate(HomeEnum.HOME_PAGE);
	};

	const handleNavToStateSelection = (hasNotSpecialtiesAtCoordinate?: boolean) => {
		const {LOCATION_STEP} = getHealthStepPosition({
			isManualLocation: true,
		});

		setBookingData(prev => ({
			...prev,
			isManualLocation: true,
		}));

		navigate(bookingUfPath, {
			state: {
				hasNotSpecialtiesAtCoordinate,
			},
		});

		const params = new URLSearchParams();
		params.set(QueryParamsEnum.STEP, `${LOCATION_STEP + 1}`);
		params.set(QueryParamsForTaggingEnum.GEOLOCATION, 'false');
		setSearchParams(params);
	};

	const handleOpenInstructions = () => {
		setOpenInstructions(true);
	};

	const handleCloseInstructions = () => {
		setOpenInstructions(false);
	};

	const handleOpenInstabilityError = () => {
		setOpenGetPositionLoading(false);
		setOpenInstabilityError(true);
	};

	const handleCloseInstabilityError = () => {
		setOpenInstabilityError(false);
	};

	const handleGoToSpecialties = () => {
		setOpenGetPositionLoading(false);
		if (!cityLocationAndSpecialtiesData) return;

		setBookingData(prev => ({
			...prev,
			email: currentEmail,
			phone: currentPhone,
			ufCode: cityLocationAndSpecialtiesData[0]?.content?.city?.uf?.toUpperCase(),
			ufName: cityLocationAndSpecialtiesData[0]?.content?.city?.state?.toUpperCase(),
			cityName: cityLocationAndSpecialtiesData[0]?.content?.city?.name?.toUpperCase(),
			typeLocalName: TypeLocalNameEnum.COORDENADAS,
			reference: '',
			coordinates: coordinatesValue,
			isManualLocation: false,
		}));

		setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
	};

	const handleRetryGetCityLocationAndSpecialties = () => {
		handleCloseInstabilityError();
		setOpenGetPositionLoading(true);
		cityLocationAndSpecialtiesRefetch();
	};

	const isUnprocessableError =
		(cityLocationAndSpecialtiesError as APIError)?.statusCode === HttpStatusCode.unprocessable;

	useEffect(() => {
		if (
			isCityLocationAndSpecialtiesError ||
			positionUnavailableOrTimeout ||
			geolocationOrPermissionIsNotSupported
		) {
			handleOpenInstabilityError();
		}
	}, [
		isCityLocationAndSpecialtiesError,
		positionUnavailableOrTimeout,
		geolocationOrPermissionIsNotSupported,
	]);

	const healthFlowWithoutSpecialtiesInCoordinates =
		isHealthFlow &&
		cityLocationAndSpecialtiesData &&
		!cityLocationAndSpecialtiesData[1]?.success;

	useEffect(() => {
		if (healthFlowWithoutSpecialtiesInCoordinates) {
			handleNavToStateSelection(true);
		}
	}, [healthFlowWithoutSpecialtiesInCoordinates]);

	const healthFlowWithSpecialtiesInCoordinates =
		isHealthFlow &&
		cityLocationAndSpecialtiesData &&
		cityLocationAndSpecialtiesData[1]?.success;

	const examOrOdontoFlowWithCoordinatesAndCityLocation =
		(isExamFlow || isOdontoFlow) &&
		Boolean(coordinatesValue) &&
		cityLocationAndSpecialtiesData &&
		cityLocationAndSpecialtiesData[0];

	const shouldGoToSpecialties =
		healthFlowWithSpecialtiesInCoordinates || examOrOdontoFlowWithCoordinatesAndCityLocation;

	useEffect(() => {
		if (shouldGoToSpecialties) {
			handleGoToSpecialties();
		}
	}, [shouldGoToSpecialties]);

	return {
		isLaptop,
		isGeolocationEnabled,
		handleGetPosition,
		permission,
		setSearchParams,
		handleNavToStateSelection,
		openInstructions,
		handleOpenInstructions,
		handleCloseInstructions,
		openGetPositionLoading,
		openInstabilityError,
		positionUnavailableOrTimeout,
		geolocationOrPermissionIsNotSupported,
		isUnprocessableError,
		handleCloseInstabilityError,
		handleRetryGetCityLocationAndSpecialties,
		handleGoHome,
		showPermissionDeniedSnackbar,
		handleClosePermissionDeniedSnackbar,
	};
}
